import { TSection, TField, TGroup } from "@smart/bridge-resources-basic";
import {
  checkHasResponse,
  Country,
  FieldType,
  MatterLayoutInfoCrossRoleRepeatableKey,
  MatterLayoutInfoParentIdKey,
  PaymentResponse,
  ResponseSyncStatus,
  UploadedFile,
} from "@smart/bridge-types-basic";
import {
  isElementOf,
  isNotNullOrUndefined,
  isNullOrUndefined,
  jsonParseOrReturn,
  logError,
  mergeByIndex,
  setNestedValue,
} from "@smart/itops-utils-basic";

import {
  convertLayoutAddressToSB,
  convertRoleAddressToSB,
} from "./address-helpers";
import { getMatchingContacts } from "./contact-helpers";
import {
  checkboxLayoutType,
  consolidatedCheckboxesLayoutType,
  defaultClientRoleId,
  matterTypeProviderId,
  roleProviderId,
  SBContactType,
  SBContact,
  MatchingContact,
  IntakeContact,
  createNewContactPlaceholder,
  clientSolicitorIds,
  LayoutField,
  IntakeMatterEvent,
} from "./types";
import {
  convertDateToSB,
  convertCurrencyToSB,
  convertPhoneNumberToSB,
  getContactInfo,
  getClientRoleName,
  buildLayoutFieldName,
  getLayoutFieldName,
  buildCheckSubLayoutMapsToIndividualClient,
  getLayoutId,
} from "./utils";

export const convertRoleValueToSB = ({
  name,
  type,
  value,
  country,
}: {
  name: string;
  type: FieldType;
  value: any;
  country: Country;
}): any => {
  try {
    switch (type) {
      case "address": {
        if (!value) return value;
        return convertRoleAddressToSB(value, country);
      }
      case "phoneNumber":
        return convertPhoneNumberToSB(country, value);
      case "date":
        return convertDateToSB(value);
      case "currency":
        return convertCurrencyToSB(value);
      case "number":
        return value.toString();
      default:
        return value;
    }
  } catch (e) {
    console.warn("Converting role value failed");
    logError(e, { name, type, value });
  }
  return undefined;
};

const getLayoutFieldComponents = (
  layoutFieldName: string,
  layoutFields: LayoutField[],
) =>
  layoutFields
    .filter((lf) => lf.name.startsWith(layoutFieldName))
    .map((lf) => lf.name.split("/").pop())
    .filter(isNotNullOrUndefined);

export const convertLayoutValueToSB = ({
  layoutField: { name, type: layoutType, possibleValues },
  type,
  value,
  country,
  matterLayoutFields,
  matterLayoutFieldName,
}: {
  layoutField: NonNullable<TField["field"]>;
  type: FieldType;
  value: any;
  country: Country;
  matterLayoutFields: LayoutField[];
  matterLayoutFieldName: string;
}): Record<string, any> => {
  try {
    switch (type) {
      case "address":
        return convertLayoutAddressToSB({
          name,
          address: value,
          country,
          supportedComponents: getLayoutFieldComponents(
            matterLayoutFieldName,
            matterLayoutFields,
          ),
        });
      case "phoneNumber":
        return { [name]: value?.number };
      case "date":
        return { [name]: convertDateToSB(value) };
      case "currency":
        return { [name]: convertCurrencyToSB(value) };
      case "number":
        return { [name]: value.toString() };
      case "choice":
      case "checkbox": {
        if (layoutType !== consolidatedCheckboxesLayoutType)
          return { [name]: value };

        const valueToUse = Array.isArray(value) ? value : [value];
        return possibleValues!.reduce(
          (aggr, possibleValue) => ({
            ...aggr,
            [`${name}/${possibleValue}`]: valueToUse.includes(possibleValue)
              ? "True"
              : "False",
          }),
          {},
        );
      }
      case "yesNo":
        if (layoutType === checkboxLayoutType)
          return { [name]: value === "yes" ? "true" : "false" };

        return { [name]: value };
      default:
        return { [name]: value };
    }
  } catch (e) {
    console.warn("Converting layout value failed");
    logError(e, { name, type, value });
  }

  return {};
};

export type ContactResponses = Record<SBContactType, Record<string, any>> & {
  hasRelation?: boolean;
  group?: Record<SBContactType, Record<string, any>>[];
  existingContactIds?: (string | undefined)[];
  reviewBeforeUpdate?: (boolean | undefined)[];
  isReviewed?: boolean;
};

export type RoleContactResponses = Record<string, ContactResponses>;

export type ResponseUriMap = Record<string, Set<string>>;

export type GroupedResponses = {
  matterType: { name?: string; location?: string; referralType?: string };
  layouts: Record<string, Record<string, any>>;
  roles: RoleContactResponses;
  files: Record<string, UploadedFile[]>;
  layoutContacts: Record<
    string,
    (Partial<Record<SBContactType, Record<string, any>>> & {
      isReviewed?: boolean;
      existingContactId?: string;
      needReviewBeforeUpdate?: boolean;
    })[]
  >;
  relationships: Record<string, RoleContactResponses>;
  events: Record<string, IntakeMatterEvent | IntakeMatterEvent[]>;
  payments: Record<string, PaymentResponse>;
  responseUris: {
    files: ResponseUriMap;
    layouts: ResponseUriMap;
    roles: ResponseUriMap;
    layoutContacts: ResponseUriMap;
    relationships: ResponseUriMap;
    events: ResponseUriMap;
    payments: ResponseUriMap;
  };
};

type FieldResponse = {
  field: TField;
  value: any;
  updatedAt: number;
  group?: TGroup;
  uri: string;
  existingItemIds?: string;
  reviewBeforeUpdate?: string;
  syncStatus?: ResponseSyncStatus;
};

type LayoutParams = {
  layoutUpdatedTime: Record<string, number>;
  layoutId: string;
  layoutValue: any;
};

const formatResponseValue = (response: FieldResponse) =>
  response.group && Array.isArray(response.value)
    ? response.value[0]
    : response.value;

const isLayoutUpdatable = (
  fieldResponse: FieldResponse,
  { layoutId, layoutUpdatedTime, layoutValue }: LayoutParams,
) =>
  !layoutUpdatedTime[`${layoutId}-${fieldResponse.field.field?.name}`] ||
  (fieldResponse.updatedAt >
    layoutUpdatedTime[`${layoutId}-${fieldResponse.field.field?.name}`] &&
  Array.isArray(layoutValue)
    ? layoutValue.some((v) => checkHasResponse(fieldResponse.field.type, v))
    : checkHasResponse(fieldResponse.field.type, layoutValue));

const getLayoutItemsToUpdate = ({
  fieldResponse,
  layoutId,
  layoutUpdatedTime,
  country,
  layoutFields,
  overrideClientIndex,
  fields,
}: {
  fieldResponse: FieldResponse;
  layoutId: string;
  layoutUpdatedTime: Record<string, number>;
  country: Country;
  layoutFields: LayoutField[];
  overrideClientIndex?: {
    index: number;
    checkShouldOverride: (nameParts: string[]) => boolean;
  };
  fields: TField[];
}): Record<string, any> | null => {
  const isGroupResponse =
    !!fieldResponse.group && Array.isArray(fieldResponse.value);
  const originalLayoutFieldName = getLayoutFieldName(
    fieldResponse.field.field!,
    layoutFields,
    fieldResponse.field.type,
  );

  if (!originalLayoutFieldName) return null;

  if (
    !isLayoutUpdatable(fieldResponse, {
      layoutId,
      layoutUpdatedTime,
      layoutValue: fieldResponse.value,
    })
  )
    return null;

  const responseValueAsArray: any[] = isGroupResponse
    ? fieldResponse.value
    : [fieldResponse.value];
  const layoutItems: Record<string, any> = {};

  // Add special item to store the value of field.layout.parentId if exists
  // This value will be used in updating back to SB layout items.
  if (fieldResponse.field.layout?.parentId) {
    layoutItems[MatterLayoutInfoParentIdKey] =
      fieldResponse.field.layout?.parentId;
  }

  const getIsCrossRoleRepeatable = () => {
    /**
     * A layout field is only considered as cross role repeatable if the following conditions are met:
     * 1. The group is repeatable
     * 2. The group has other role fields with the same role name
     * 3. The the field's parent role is the same as the other role fields' role in the group
     */
    if (!fieldResponse.group?.repeatable) return false;
    const groupRoleFields = fields.filter(
      (f) =>
        f.groupUri === fieldResponse.group?.uri &&
        f.layout?.providerId === roleProviderId &&
        !f.layout.parentId,
    );
    const groupRoleNames = new Set(groupRoleFields.map((f) => f.layout?.id));
    if (groupRoleNames.size !== 1) return false;
    const groupRoleName = groupRoleNames.values().next().value;
    return groupRoleName === fieldResponse.field.layout?.parentId;
  };
  const isCrossRoleRepeatable = getIsCrossRoleRepeatable();
  if (isCrossRoleRepeatable) {
    layoutItems[MatterLayoutInfoCrossRoleRepeatableKey] = [];
  }

  responseValueAsArray.forEach((value, index: number) => {
    const layoutFieldName = buildLayoutFieldName({
      name: originalLayoutFieldName,
      index: isCrossRoleRepeatable ? 0 : index,
      overrideClientIndex,
    });

    const convertedLayoutValue = convertLayoutValueToSB({
      layoutField: {
        ...fieldResponse.field.field!,
        name: layoutFieldName,
      },
      type: fieldResponse.field.type,
      value,
      country,
      matterLayoutFieldName: originalLayoutFieldName,
      matterLayoutFields: layoutFields,
    });

    if (isCrossRoleRepeatable) {
      layoutItems[MatterLayoutInfoCrossRoleRepeatableKey][index] =
        layoutItems[MatterLayoutInfoCrossRoleRepeatableKey][index] || {};
      Object.assign(
        layoutItems[MatterLayoutInfoCrossRoleRepeatableKey][index],
        convertedLayoutValue,
      );
    } else if (!layoutItems[layoutFieldName]) {
      Object.assign(layoutItems, convertedLayoutValue);
    }
  });

  return layoutItems;
};

const updateResponseUriMap = ({
  responseUris,
  map,
  key,
  uri,
}: {
  responseUris: GroupedResponses["responseUris"];
  map: keyof GroupedResponses["responseUris"];
  key: string;
  uri: string;
}) => {
  if (!responseUris[map]) {
    responseUris[map] = { [key]: new Set([uri]) };
  } else if (!responseUris[map][key]) {
    responseUris[map][key] = new Set([uri]);
  } else {
    responseUris[map][key].add(uri);
  }
};

const processLayoutContact = (
  response: FieldResponse,
  current: GroupedResponses["layoutContacts"],
  country: Country,
  responseUris: GroupedResponses["responseUris"],
) => {
  // Also skip "Role" field of "company/contact" as it is reserved for adding contact to a company
  if (
    response.group?.type !== "layoutContact" ||
    !response.field.groupUri ||
    !response.field.field ||
    response.group.field?.name === "company/contact"
  )
    return undefined; // return undefined to let function "groupReponse" process further

  const [contactType, key] = getContactInfo(response.field.field.name || "");
  if (!["person", "company", "trust"].includes(contactType)) return undefined;

  current[response.field.groupUri] = current[response.field.groupUri] || [];

  const parsedExistingItemIds = jsonParseOrReturn(
    response.existingItemIds,
  ) as string[];
  const existingContactIds = Array.isArray(parsedExistingItemIds)
    ? parsedExistingItemIds
    : [];
  const parsedReviewBeforeUpdate = jsonParseOrReturn(
    response.reviewBeforeUpdate,
  ) as (boolean | undefined)[];
  const reviewBeforeUpdate = Array.isArray(parsedReviewBeforeUpdate)
    ? parsedReviewBeforeUpdate
    : [];

  const responseAsArray = Array.isArray(response.value)
    ? response.value
    : [response.value];
  responseAsArray.forEach((v: any, index: number) => {
    if (!current[response.field.groupUri!][index]) {
      current[response.field.groupUri!].push({
        person: {},
        company: {},
        trust: {},
      });
    }
    setNestedValue(
      current[response.field.groupUri!][index][contactType],
      key,
      convertRoleValueToSB({
        name: response.field.field!.name,
        type: response.field.type,
        value: v,
        country,
      }),
    );
    current[response.field.groupUri!][index].isReviewed =
      response.syncStatus === "reviewed";
    current[response.field.groupUri!][index].existingContactId =
      existingContactIds[index] !== createNewContactPlaceholder
        ? existingContactIds[index]
        : undefined;
    current[response.field.groupUri!][index].needReviewBeforeUpdate =
      reviewBeforeUpdate[index];
  });

  updateResponseUriMap({
    responseUris,
    map: "layoutContacts",
    key: response.field.groupUri,
    uri: response.uri,
  });

  return current;
};

const processMatterTypeProvider = ({
  response,
  current,
}: {
  response: FieldResponse;
  current: GroupedResponses["matterType"];
}) => {
  if (!response.field.layout || !response.field.field) return current;

  const [group, name] = response.field.field.name.split("/");

  if (
    response.field.layout.providerId === matterTypeProviderId &&
    group === "matterType" &&
    isElementOf(["name", "location", "referralType"] as const)(name)
  ) {
    current[name] = response.value;
  }

  return current;
};

const processLayoutProvider = async ({
  response,
  current,
  updatedTime,
  country,
  responseUris,
  getLayoutFields,
  clientRoleId,
  sentToIndividualClientIndex,
  fields,
  layoutFieldsCache,
}: {
  response: FieldResponse;
  current: GroupedResponses["layouts"];
  updatedTime: Record<string, number>;
  country: Country;
  responseUris: GroupedResponses["responseUris"];
  getLayoutFields: ({
    layoutId,
    providerId,
  }: {
    layoutId: string;
    providerId: string;
  }) => Promise<LayoutField[]>;
  clientRoleId: string | undefined;
  sentToIndividualClientIndex: number;
  fields: TField[];
  layoutFieldsCache: Record<string, LayoutField[]>;
}) => {
  if (
    response.field.layout?.providerId === matterTypeProviderId ||
    !response.field.layout ||
    !response.field.field
  )
    return current;

  const layoutId = getLayoutId(response.field);

  if (!layoutId) return current;

  current[layoutId] = current[layoutId] || {};

  // Use cached layout fields to avoid duplicate API calls and 429 errors
  if (!layoutFieldsCache[layoutId]) {
    layoutFieldsCache[layoutId] = await getLayoutFields({
      layoutId,
      providerId: response.field.layout.providerId,
    });
  }
  const layoutFields = layoutFieldsCache[layoutId];

  /**
   * We map the client's sub-layout to the receiving client when there are multiple clients
   * and the recipient is one of the clients.
   */
  const shouldOverrideClientRoleIndex =
    sentToIndividualClientIndex !== -1 &&
    response.field.layout.parentId &&
    clientRoleId &&
    response.field.layout.parentId === clientRoleId;
  const layoutItemsToUpdate = getLayoutItemsToUpdate({
    fieldResponse: response,
    layoutId,
    layoutUpdatedTime: updatedTime,
    country,
    layoutFields,
    overrideClientIndex: shouldOverrideClientRoleIndex
      ? {
          index: sentToIndividualClientIndex,
          checkShouldOverride: buildCheckSubLayoutMapsToIndividualClient({
            isGroupRepeatable: !!response.group?.repeatable,
          }),
        }
      : undefined,
    fields,
  });

  if (layoutItemsToUpdate) {
    Object.entries(layoutItemsToUpdate).forEach(([key, value]) => {
      const existing = current[layoutId][key];
      // CrossRoleRepeatable is an array where each index corresponds to a role
      // member. Multiple layout fields contribute to the same index, so we
      // per-index merge rather than replace to preserve entries from prior fields.
      if (
        key === MatterLayoutInfoCrossRoleRepeatableKey &&
        Array.isArray(existing) &&
        Array.isArray(value)
      ) {
        current[layoutId][key] = mergeByIndex(existing, value);
      } else {
        current[layoutId][key] = value;
      }
    });

    updatedTime[`${layoutId}-${response.field.field.name}`] =
      response.updatedAt;
  }

  updateResponseUriMap({
    responseUris,
    map: "layouts",
    key: layoutId,
    uri: response.uri,
  });

  return current;
};

const updateRoleContactResponses = ({
  response,
  contactResponses,
  updatedTime,
  country,
}: {
  response: FieldResponse;
  contactResponses: ContactResponses;
  updatedTime: Record<SBContactType, Record<string, number>>;
  country: Country;
}) => {
  const isGroupFieldResponseEmpty =
    response.group &&
    (isNullOrUndefined(response.value) ||
      (Array.isArray(response.value) &&
        response.value.filter((v) => !isNullOrUndefined(v)).length === 0));
  if (!response.field.field || isGroupFieldResponseEmpty)
    return contactResponses;

  const [contactType, key] = getContactInfo(response.field.field.name);

  if (response.group?.repeatable && Array.isArray(response.value)) {
    if (!contactResponses?.group) {
      contactResponses.group = [{ person: {}, company: {}, trust: {} }];
    }

    response.value.forEach((v, index) => {
      const currentRoleGroup = contactResponses.group!;
      if (currentRoleGroup.length === index) {
        currentRoleGroup.push({
          person: {},
          company: {},
          trust: {},
        });
      }

      const contactInGroup = currentRoleGroup[index];
      setNestedValue(
        contactInGroup[contactType],
        key,
        convertRoleValueToSB({
          name: response.field.field!.name,
          type: response.field.type,
          value: v,
          country,
        }),
      );
    });
  } else {
    const roleValue = formatResponseValue(response);
    const isGroupContact = !!contactResponses.group;
    const isLatestResponse =
      !updatedTime[contactType][key] ||
      (response.updatedAt > updatedTime[contactType][key] &&
        checkHasResponse(response.field.type, roleValue));

    // If the role has multiple contacts, ignore individual layout
    if (!isGroupContact && isLatestResponse) {
      setNestedValue(
        contactResponses[contactType],
        key,
        convertRoleValueToSB({
          name: response.field.field.name,
          type: response.field.type,
          value: roleValue,
          country,
        }),
      );
      updatedTime[contactType][key] = response.updatedAt;
    }
  }

  if (response.group?.field?.name === "company/contact") {
    contactResponses.hasRelation = true;
  }
  contactResponses.isReviewed = response.syncStatus === "reviewed";

  if (
    response.existingItemIds &&
    !contactResponses.existingContactIds?.length
  ) {
    const parsed = jsonParseOrReturn(response.existingItemIds) as string[];
    contactResponses.existingContactIds = Array.isArray(parsed)
      ? parsed.map((id) =>
          id && createNewContactPlaceholder !== id ? id : undefined,
        )
      : undefined;
  }

  if (response.reviewBeforeUpdate && !contactResponses.reviewBeforeUpdate) {
    const parsed = jsonParseOrReturn(response.reviewBeforeUpdate) as (
      | boolean
      | undefined
    )[];
    contactResponses.reviewBeforeUpdate = Array.isArray(parsed)
      ? parsed
      : undefined;
  }

  return contactResponses;
};

const processRoleProvider = ({
  response,
  current,
  updatedTime,
  country,
  checkSkip,
  roleIdOverride,
  responseUris,
}: {
  response: FieldResponse;
  current: GroupedResponses["roles"];
  updatedTime: Record<string, Record<SBContactType, Record<string, number>>>;
  country: Country;
  checkSkip?: (field: FieldResponse["field"]) => boolean;
  roleIdOverride?: string;
  responseUris: GroupedResponses["responseUris"];
}) => {
  if (
    response.field.layout?.providerId !== roleProviderId ||
    !response.field.field ||
    response.field.layout.parentId ||
    checkSkip?.(response.field)
  )
    return current;

  const roleId = roleIdOverride || response.field.layout!.id;

  current[roleId] = current[roleId] || {
    person: {},
    company: {},
    trust: {},
  };
  updatedTime[roleId] = updatedTime[roleId] || {
    person: {},
    company: {},
    trust: {},
  };

  current[roleId] = updateRoleContactResponses({
    response,
    contactResponses: current[roleId],
    updatedTime: updatedTime[roleId],
    country,
  });

  updateResponseUriMap({
    responseUris,
    map: "roles",
    key: roleId,
    uri: response.uri,
  });

  return current;
};

const processRelationshipRoleProvider = ({
  response,
  current,
  updatedTime,
  country,
  responseUris,
}: {
  response: FieldResponse;
  current: GroupedResponses["relationships"];
  updatedTime: Record<
    string,
    Record<string, Record<SBContactType, Record<string, number>>>
  >;
  country: Country;
  responseUris: GroupedResponses["responseUris"];
}) => {
  if (
    response.field.layout?.providerId !== roleProviderId ||
    !response.field.field ||
    !response.field.layout.parentId ||
    response.field.layout.parentProviderId !== roleProviderId
  )
    return current;

  const { id: relationshipRoleId, parentId: roleId } = response.field.layout;

  current[roleId] = current[roleId] || {
    [relationshipRoleId]: { person: {}, company: {}, trust: {} },
  };
  current[roleId][relationshipRoleId] = current[roleId][relationshipRoleId] || {
    person: {},
    company: {},
    trust: {},
  };

  updatedTime[roleId] = updatedTime[roleId] || {
    [relationshipRoleId]: { person: {}, company: {}, trust: {} },
  };
  updatedTime[roleId][relationshipRoleId] = updatedTime[roleId][
    relationshipRoleId
  ] || { person: {}, company: {}, trust: {} };

  current[roleId][relationshipRoleId] = updateRoleContactResponses({
    response,
    contactResponses: current[roleId][relationshipRoleId],
    updatedTime: updatedTime[roleId][relationshipRoleId],
    country,
  });

  updateResponseUriMap({
    responseUris,
    map: "relationships",
    key: `${roleId}/${relationshipRoleId}`,
    uri: response.uri,
  });

  return current;
};

export const groupResponse = async ({
  clientRoleId,
  sentToIndividualClientIndex = -1,
  isFormCategoryDifferent,
  formMatterTypeRepresentatives,
  sections,
  fieldResponses,
  country,
  getLayoutFields,
  fields,
}: {
  clientRoleId: string | undefined;
  sentToIndividualClientIndex?: number;
  isFormCategoryDifferent: boolean;
  formMatterTypeRepresentatives?: string[];
  sections: TSection[];
  fieldResponses: FieldResponse[];
  country: Country;
  getLayoutFields: ({
    layoutId,
    providerId,
  }: {
    layoutId: string;
    providerId: string;
  }) => Promise<LayoutField[]>;
  fields: TField[];
}): Promise<GroupedResponses> => {
  const layoutUpdatedTime: Record<string, number> = {};
  const roleUpdatedTime: Record<
    string,
    Record<SBContactType, Record<string, number>>
  > = {};
  const relationshipsUpdatedTime: Record<
    string,
    Record<string, Record<SBContactType, Record<string, number>>>
  > = {};

  const layoutFieldsCache: Record<string, LayoutField[]> = {};

  const grouped = await fieldResponses.reduce(
    async (agg, r) => {
      const resolvedAgg = await agg;
      if (!r.field) return resolvedAgg;

      const {
        matterType,
        layouts,
        relationships,
        roles,
        files,
        layoutContacts,
        events,
        payments,
        responseUris,
      } = resolvedAgg;

      if (r.field.type === "file") {
        let uploadedFiles: UploadedFile[];

        // For backwards compatibility
        if (
          !Array.isArray(r.value) &&
          (r.value as unknown as { downloadUrl: string })?.downloadUrl
        ) {
          uploadedFiles = [
            {
              ...(r.value as Omit<UploadedFile, "uploadStatus">),
              uploadStatus: "uploaded" as const,
            },
          ];
        } else if (Array.isArray(r.value)) {
          uploadedFiles = (r.value as UploadedFile[])
            .flat()
            .filter((f) => f?.uploadStatus === "uploaded");
        } else {
          console.warn("Invalid file value:", r.value, "type:", typeof r.value);
          uploadedFiles = [];
        }
        if (!uploadedFiles?.length) return resolvedAgg;

        updateResponseUriMap({
          responseUris,
          map: "files",
          key: r.field.uri,
          uri: r.uri,
        });

        return {
          ...resolvedAgg,
          files: {
            ...files,
            [r.field.uri]: uploadedFiles,
          },
        };
      }

      if (r.field.type === "appointment" && r.value) {
        updateResponseUriMap({
          responseUris,
          map: "events",
          key: r.field.uri,
          uri: r.uri,
        });

        return {
          ...resolvedAgg,
          events: {
            ...events,
            [r.field.uri]: r.value,
          },
        };
      }

      if (r.field.type === "payment") {
        updateResponseUriMap({
          responseUris,
          map: "payments",
          key: r.field.uri,
          uri: r.uri,
        });

        return {
          ...resolvedAgg,
          payments: {
            ...payments,
            [r.field.uri]: r.value,
          },
        };
      }

      if (isFormCategoryDifferent) {
        const formClientRoleName = getClientRoleName(
          sections,
          fieldResponses.map(({ field }) => field),
          formMatterTypeRepresentatives,
        );

        return {
          ...resolvedAgg,
          roles: processRoleProvider({
            roleIdOverride:
              r.field.layout?.id === formClientRoleName
                ? defaultClientRoleId
                : undefined,
            checkSkip: ({ layout }) =>
              ![formClientRoleName, defaultClientRoleId].includes(layout?.id),
            response: r,
            current: roles,
            updatedTime: roleUpdatedTime,
            country,
            responseUris,
          }),
        };
      }

      const updatedLayoutContacts = processLayoutContact(
        r,
        layoutContacts,
        country,
        responseUris,
      );
      if (updatedLayoutContacts) {
        return {
          ...resolvedAgg,
          layoutContacts: updatedLayoutContacts,
        };
      }

      return {
        ...resolvedAgg,
        matterType: processMatterTypeProvider({
          response: r,
          current: matterType,
        }),
        layouts: await processLayoutProvider({
          response: r,
          current: layouts,
          updatedTime: layoutUpdatedTime,
          country,
          responseUris,
          getLayoutFields,
          clientRoleId,
          sentToIndividualClientIndex,
          fields,
          layoutFieldsCache,
        }),
        roles: processRoleProvider({
          response: r,
          current: roles,
          updatedTime: roleUpdatedTime,
          country,
          responseUris,
        }),
        relationships: processRelationshipRoleProvider({
          response: r,
          current: relationships,
          updatedTime: relationshipsUpdatedTime,
          country,
          responseUris,
        }),
      };
    },
    Promise.resolve({
      matterType: {} as GroupedResponses["matterType"],
      layouts: {} as GroupedResponses["layouts"],
      roles: {} as GroupedResponses["roles"],
      files: {} as GroupedResponses["files"],
      layoutContacts: {} as GroupedResponses["layoutContacts"],
      relationships: {} as GroupedResponses["relationships"],
      events: {} as GroupedResponses["events"],
      payments: {} as GroupedResponses["payments"],
      responseUris: {
        files: {},
        roles: {},
        layouts: {},
        layoutContacts: {},
        relationships: {},
      } as GroupedResponses["responseUris"],
    }),
  );

  return grouped;
};

const combineMatchingContacts = (
  current: MatchingContact[],
  incoming: MatchingContact[],
) =>
  incoming.forEach((mc) => {
    if (!current.find((contact) => contact.id === mc.id)) {
      current.push(mc);
    }
  });

export const lookupMatchingContacts = async ({
  input,
  search,
}: {
  input: IntakeContact;
  search: (searchTerms: {
    names?: string[];
    emails?: string[];
    phones?: string[];
  }) => Promise<SBContact[]>;
}): Promise<MatchingContact[]> => {
  const { person, company, trust } = input;
  if (!person && !company && !trust) return [];

  const wildcard = /^[*]+$/;
  const searchTermName =
    person?.firstName || person?.lastName
      ? `*${person.firstName || ""}*${person.lastName || ""}*`
      : `*${company?.name || trust?.name || ""}*`;
  const rawEmail = person?.email || company?.email;
  const searchTermEmail =
    typeof rawEmail === "string" ? rawEmail.trim() : undefined;
  const getSearchTermPhone = () => {
    if (person?.phone)
      return `${person?.phone?.areaCode || ""}${person?.phone?.number || ""}`;
    if (company?.phone)
      return `${company?.phone?.areaCode || ""}${company?.phone?.number || ""}`;
    if (trust?.phone)
      return `${trust?.phone?.areaCode || ""}${trust?.phone?.number || ""}`;
    return "";
  };
  const searchTermPhone = getSearchTermPhone();
  const searchTermCell = `${person?.cell?.areaCode || ""}${person?.cell?.number || ""}`;

  // search API only accepts name/email/phone, so a duplicate that only shares an address can never be retrieved or flagged
  let possibleMatches: MatchingContact[] = [];
  let searchResult: SBContact[] = [];
  let nameSearchMatches = 0;

  if (!searchTermName.match(wildcard) && searchTermEmail) {
    searchResult = await search({
      names: [searchTermName],
      emails: [searchTermEmail],
    });
    possibleMatches = getMatchingContacts(input, searchResult);
    nameSearchMatches = possibleMatches.length;
    const exactMatch = possibleMatches.find(
      (match) => match.matchType === "exact",
    );
    if (exactMatch) return [exactMatch];
  }

  if (!searchTermName.match(wildcard)) {
    searchResult = await search({
      names: [searchTermName],
    });
    const nameMatches = getMatchingContacts(input, searchResult);
    nameSearchMatches += nameMatches.length;
    combineMatchingContacts(possibleMatches, nameMatches);
  }

  if (searchTermEmail) {
    searchResult = await search({
      emails: [searchTermEmail],
    });
    combineMatchingContacts(
      possibleMatches,
      getMatchingContacts(input, searchResult),
    );
  }

  if (searchTermPhone) {
    searchResult = await search({
      phones: [searchTermPhone.replaceAll(" ", "").replaceAll("-", "").trim()],
    });
    combineMatchingContacts(
      possibleMatches,
      getMatchingContacts(input, searchResult),
    );
  }

  if (searchTermCell) {
    searchResult = await search({
      phones: [searchTermCell.replaceAll(" ", "").replaceAll("-", "").trim()],
    });
    combineMatchingContacts(
      possibleMatches,
      getMatchingContacts(input, searchResult),
    );
  }

  // `*first*last*` misses existing names that are a substring of the input ("Marie Peak" vs input "Marie Peakson"), so retry each token alone when nothing matched
  if (nameSearchMatches === 0 && (person?.firstName || person?.lastName)) {
    const fallbackTerms = [
      person.firstName ? `*${person.firstName}*` : "",
      person.lastName ? `*${person.lastName}*` : "",
    ].filter((term) => term && !term.match(wildcard));

    for (const term of fallbackTerms) {
      searchResult = await search({ names: [term] });
      const fallbackMatches = getMatchingContacts(input, searchResult);
      combineMatchingContacts(possibleMatches, fallbackMatches);
      if (fallbackMatches.length > 0) break;
    }
  }

  console.debug("Total matching contacts: ", possibleMatches.length);
  console.debug(
    "Exact matching contacts count: ",
    possibleMatches.filter((mc) => mc.matchType === "exact").length,
  );
  console.debug(
    "Partial matching contacts count: ",
    possibleMatches.filter((mc) => mc.matchType === "partial").length,
  );

  return possibleMatches;
};

export const createFilterClientSolicitor =
  (clientRoleName: string | undefined) =>
  (fieldResponse: {
    field: {
      layout?: {
        id: string;
        parentId?: string;
      };
    };
  }) => {
    if (!clientRoleName) return true;

    const isClientSolicitor =
      fieldResponse.field.layout?.parentId === clientRoleName &&
      clientSolicitorIds.includes(fieldResponse.field.layout.id);
    return !isClientSolicitor;
  };
